/**
 * Damage Calculator Page - Pokemon damage calculation tool
 * Based on the Gen 5+ damage formula used in Pokemon Scarlet/Violet
 */
import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Swords, Shield, Zap, Flame, Droplets, Sparkles, RefreshCw, Info, ChevronDown, Search, ChevronUp, Package } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { usePokemon, TYPE_CHART } from '../hooks/usePokemon';
import { cn } from '../lib/utils';
import { trackEvent } from '../lib/analytics';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';

// Type effectiveness chart (for damage calculation)
const TYPE_EFFECTIVENESS = {
  normal: { ghost: 0, rock: 0.5, steel: 0.5 },
  fire: { fire: 0.5, water: 0.5, grass: 2, ice: 2, bug: 2, rock: 0.5, dragon: 0.5, steel: 2 },
  water: { fire: 2, water: 0.5, grass: 0.5, ground: 2, rock: 2, dragon: 0.5 },
  electric: { water: 2, electric: 0.5, grass: 0.5, ground: 0, flying: 2, dragon: 0.5 },
  grass: { fire: 0.5, water: 2, grass: 0.5, poison: 0.5, ground: 2, flying: 0.5, bug: 0.5, rock: 2, dragon: 0.5, steel: 0.5 },
  ice: { fire: 0.5, water: 0.5, grass: 2, ice: 0.5, ground: 2, flying: 2, dragon: 2, steel: 0.5 },
  fighting: { normal: 2, ice: 2, poison: 0.5, flying: 0.5, psychic: 0.5, bug: 0.5, rock: 2, ghost: 0, dark: 2, steel: 2, fairy: 0.5 },
  poison: { grass: 2, poison: 0.5, ground: 0.5, rock: 0.5, ghost: 0.5, steel: 0, fairy: 2 },
  ground: { fire: 2, electric: 2, grass: 0.5, poison: 2, flying: 0, bug: 0.5, rock: 2, steel: 2 },
  flying: { electric: 0.5, grass: 2, fighting: 2, bug: 2, rock: 0.5, steel: 0.5 },
  psychic: { fighting: 2, poison: 2, psychic: 0.5, dark: 0, steel: 0.5 },
  bug: { fire: 0.5, grass: 2, fighting: 0.5, poison: 0.5, flying: 0.5, psychic: 2, ghost: 0.5, dark: 2, steel: 0.5, fairy: 0.5 },
  rock: { fire: 2, ice: 2, fighting: 0.5, ground: 0.5, flying: 2, bug: 2, steel: 0.5 },
  ghost: { normal: 0, psychic: 2, ghost: 2, dark: 0.5 },
  dragon: { dragon: 2, steel: 0.5, fairy: 0 },
  dark: { fighting: 0.5, psychic: 2, ghost: 2, dark: 0.5, fairy: 0.5 },
  steel: { fire: 0.5, water: 0.5, electric: 0.5, ice: 2, rock: 2, steel: 0.5, fairy: 2 },
  fairy: { fire: 0.5, fighting: 2, poison: 0.5, dragon: 2, dark: 2, steel: 0.5 }
};

// Stat stage multipliers
const STAT_STAGES = {
  '-6': 2/8, '-5': 2/7, '-4': 2/6, '-3': 2/5, '-2': 2/4, '-1': 2/3,
  '0': 1,
  '1': 3/2, '2': 4/2, '3': 5/2, '4': 6/2, '5': 7/2, '6': 8/2
};

// Weather conditions
const WEATHER_OPTIONS = [
  { value: 'none', label: { en: 'None', es: 'Ninguno' } },
  { value: 'sun', label: { en: 'Harsh Sunlight', es: 'Sol intenso' } },
  { value: 'rain', label: { en: 'Rain', es: 'Lluvia' } },
  { value: 'sand', label: { en: 'Sandstorm', es: 'Tormenta de arena' } },
  { value: 'hail', label: { en: 'Snow/Hail', es: 'Nieve/Granizo' } }
];

// Terrain options
const TERRAIN_OPTIONS = [
  { value: 'none', label: { en: 'None', es: 'Ninguno' } },
  { value: 'electric', label: { en: 'Electric Terrain', es: 'Campo Eléctrico' } },
  { value: 'grassy', label: { en: 'Grassy Terrain', es: 'Campo de Hierba' } },
  { value: 'psychic', label: { en: 'Psychic Terrain', es: 'Campo Psíquico' } },
  { value: 'misty', label: { en: 'Misty Terrain', es: 'Campo de Niebla' } }
];

// Type colors
const TYPE_COLORS = {
  normal: 'bg-gray-400', fire: 'bg-orange-500', water: 'bg-blue-500',
  electric: 'bg-yellow-400', grass: 'bg-green-500', ice: 'bg-cyan-300',
  fighting: 'bg-red-700', poison: 'bg-purple-500', ground: 'bg-amber-600',
  flying: 'bg-indigo-300', psychic: 'bg-pink-500', bug: 'bg-lime-500',
  rock: 'bg-stone-500', ghost: 'bg-violet-700', dragon: 'bg-indigo-700',
  dark: 'bg-stone-700', steel: 'bg-slate-400', fairy: 'bg-pink-300'
};

// Spanish type names
const TYPE_NAMES = {
  normal: { en: 'Normal', es: 'Normal' }, fire: { en: 'Fire', es: 'Fuego' },
  water: { en: 'Water', es: 'Agua' }, electric: { en: 'Electric', es: 'Eléctrico' },
  grass: { en: 'Grass', es: 'Planta' }, ice: { en: 'Ice', es: 'Hielo' },
  fighting: { en: 'Fighting', es: 'Lucha' }, poison: { en: 'Poison', es: 'Veneno' },
  ground: { en: 'Ground', es: 'Tierra' }, flying: { en: 'Flying', es: 'Volador' },
  psychic: { en: 'Psychic', es: 'Psíquico' }, bug: { en: 'Bug', es: 'Bicho' },
  rock: { en: 'Rock', es: 'Roca' }, ghost: { en: 'Ghost', es: 'Fantasma' },
  dragon: { en: 'Dragon', es: 'Dragón' }, dark: { en: 'Dark', es: 'Siniestro' },
  steel: { en: 'Steel', es: 'Acero' }, fairy: { en: 'Fairy', es: 'Hada' }
};

// ---------------------------------------------------------------------------
// Natures: stat boosted / dropped (null = neutral)
// ---------------------------------------------------------------------------
const NATURES = {
  hardy:   { boost: null, drop: null, en: 'Hardy', es: 'Fuerte' },
  lonely:  { boost: 'attack', drop: 'defense', en: 'Lonely', es: 'Huraña' },
  brave:   { boost: 'attack', drop: 'speed', en: 'Brave', es: 'Audaz' },
  adamant: { boost: 'attack', drop: 'special-attack', en: 'Adamant', es: 'Firme' },
  naughty: { boost: 'attack', drop: 'special-defense', en: 'Naughty', es: 'Pícara' },
  bold:    { boost: 'defense', drop: 'attack', en: 'Bold', es: 'Osada' },
  docile:  { boost: null, drop: null, en: 'Docile', es: 'Dócil' },
  relaxed: { boost: 'defense', drop: 'speed', en: 'Relaxed', es: 'Plácida' },
  impish:  { boost: 'defense', drop: 'special-attack', en: 'Impish', es: 'Agitada' },
  lax:     { boost: 'defense', drop: 'special-defense', en: 'Lax', es: 'Floja' },
  timid:   { boost: 'speed', drop: 'attack', en: 'Timid', es: 'Miedosa' },
  hasty:   { boost: 'speed', drop: 'defense', en: 'Hasty', es: 'Activa' },
  serious: { boost: null, drop: null, en: 'Serious', es: 'Seria' },
  jolly:   { boost: 'speed', drop: 'special-attack', en: 'Jolly', es: 'Alegre' },
  naive:   { boost: 'speed', drop: 'special-defense', en: 'Naive', es: 'Ingenua' },
  modest:  { boost: 'special-attack', drop: 'attack', en: 'Modest', es: 'Modesta' },
  mild:    { boost: 'special-attack', drop: 'defense', en: 'Mild', es: 'Afable' },
  quiet:   { boost: 'special-attack', drop: 'speed', en: 'Quiet', es: 'Mansa' },
  bashful: { boost: null, drop: null, en: 'Bashful', es: 'Tímida' },
  rash:    { boost: 'special-attack', drop: 'special-defense', en: 'Rash', es: 'Alocada' },
  calm:    { boost: 'special-defense', drop: 'attack', en: 'Calm', es: 'Serena' },
  gentle:  { boost: 'special-defense', drop: 'defense', en: 'Gentle', es: 'Amable' },
  sassy:   { boost: 'special-defense', drop: 'speed', en: 'Sassy', es: 'Grosera' },
  careful: { boost: 'special-defense', drop: 'special-attack', en: 'Careful', es: 'Cauta' },
  quirky:  { boost: null, drop: null, en: 'Quirky', es: 'Rara' },
};

const NATURE_OPTIONS = Object.keys(NATURES).map((n) => {
  const { boost, drop, en, es } = NATURES[n];
  // Use English by default, can be translated dynamically if needed
  const label = en || n.charAt(0).toUpperCase() + n.slice(1);
  const tag = boost ? ` (+${boost.replace('special-', 'Sp.')}, -${drop.replace('special-', 'Sp.')})` : '';
  return { value: n, label: label + tag, labelEs: es + (boost ? ` (+${boost.replace('special-', 'Esp.')}, -${drop.replace('special-', 'Esp.')})` : '') };
});

/** Get nature multiplier for a given stat key */
function getNatureMod(nature, statKey) {
  const n = NATURES[nature];
  if (!n) return 1;
  if (n.boost === statKey) return 1.1;
  if (n.drop === statKey) return 0.9;
  return 1;
}

/** Calculate actual in-game stat (non-HP) */
function calcStat(base, level, ev, iv, nature, statKey) {
  const raw = Math.floor((2 * base + iv + Math.floor(ev / 4)) * level / 100) + 5;
  return Math.floor(raw * getNatureMod(nature, statKey));
}

/** Calculate HP stat */
function calcHP(base, level, ev, iv) {
  if (base === 1) return 1; // Shedinja
  return Math.floor((2 * base + iv + Math.floor(ev / 4)) * level / 100) + level + 10;
}

// ---------------------------------------------------------------------------
// Held Items
// ---------------------------------------------------------------------------
const HELD_ITEMS = [
  { value: 'none',          label: { es: 'Sin objeto',        en: 'No Item' } },
  { value: 'life-orb',      label: { es: 'Orbe Vida (×1.3)',  en: 'Life Orb (×1.3)' } },
  { value: 'choice-band',   label: { es: 'Banda Elegida (Fís. ×1.5)', en: 'Choice Band (Phys. ×1.5)' } },
  { value: 'choice-specs',  label: { es: 'Gafas Elegidas (Esp. ×1.5)', en: 'Choice Specs (Spec. ×1.5)' } },
  { value: 'muscle-band',   label: { es: 'Cinta Músculo (Fís. ×1.1)', en: 'Muscle Band (Phys. ×1.1)' } },
  { value: 'wise-glasses',  label: { es: 'Sabidugafas (Esp. ×1.1)',  en: 'Wise Glasses (Spec. ×1.1)' } },
  { value: 'expert-belt',   label: { es: 'Cinturón Experto (SE ×1.2)', en: 'Expert Belt (SE ×1.2)' } },
  { value: 'charcoal',      label: { es: 'Carbón (Fuego ×1.2)',   en: 'Charcoal (Fire ×1.2)' } },
  { value: 'mystic-water',  label: { es: 'Agua Mística (Agua ×1.2)', en: 'Mystic Water (Water ×1.2)' } },
  { value: 'miracle-seed',  label: { es: 'Semilla Milagro (Planta ×1.2)', en: 'Miracle Seed (Grass ×1.2)' } },
  { value: 'magnet',        label: { es: 'Imán (Eléctrico ×1.2)', en: 'Magnet (Electric ×1.2)' } },
  { value: 'never-melt-ice',label: { es: 'Hielo Eterno (Hielo ×1.2)', en: 'Never-Melt Ice (Ice ×1.2)' } },
  { value: 'black-belt',    label: { es: 'Cinta Negra (Lucha ×1.2)', en: 'Black Belt (Fighting ×1.2)' } },
  { value: 'soft-sand',     label: { es: 'Arena Blanda (Tierra ×1.2)', en: 'Soft Sand (Ground ×1.2)' } },
  { value: 'sharp-beak',    label: { es: 'Pico Agudo (Volador ×1.2)', en: 'Sharp Beak (Flying ×1.2)' } },
  { value: 'twisted-spoon', label: { es: 'Cuchara Torcida (Psíquico ×1.2)', en: 'Twisted Spoon (Psychic ×1.2)' } },
  { value: 'poison-barb',   label: { es: 'Púas Veneno (Veneno ×1.2)', en: 'Poison Barb (Poison ×1.2)' } },
  { value: 'silk-scarf',    label: { es: 'Pañuelo Seda (Normal ×1.2)', en: 'Silk Scarf (Normal ×1.2)' } },
  { value: 'silver-powder', label: { es: 'Polvo Plata (Bicho ×1.2)', en: 'Silver Powder (Bug ×1.2)' } },
  { value: 'hard-stone',    label: { es: 'Piedra Dura (Roca ×1.2)', en: 'Hard Stone (Rock ×1.2)' } },
  { value: 'spell-tag',     label: { es: 'Amuleto Raro (Fantasma ×1.2)', en: 'Spell Tag (Ghost ×1.2)' } },
  { value: 'dragon-fang',   label: { es: 'Colmillo Dragón (Dragón ×1.2)', en: 'Dragon Fang (Dragon ×1.2)' } },
  { value: 'black-glasses', label: { es: 'Gafas Negras (Siniestro ×1.2)', en: 'Black Glasses (Dark ×1.2)' } },
  { value: 'metal-coat',    label: { es: 'Revestimiento (Acero ×1.2)', en: 'Metal Coat (Steel ×1.2)' } },
  { value: 'fairy-feather', label: { es: 'Pluma Hada (Hada ×1.2)', en: 'Fairy Feather (Fairy ×1.2)' } },
];

const ITEM_TYPE_BOOST = {
  'charcoal': 'fire', 'mystic-water': 'water', 'miracle-seed': 'grass',
  'magnet': 'electric', 'never-melt-ice': 'ice', 'black-belt': 'fighting',
  'soft-sand': 'ground', 'sharp-beak': 'flying', 'twisted-spoon': 'psychic',
  'poison-barb': 'poison', 'silk-scarf': 'normal', 'silver-powder': 'bug',
  'hard-stone': 'rock', 'spell-tag': 'ghost', 'dragon-fang': 'dragon',
  'black-glasses': 'dark', 'metal-coat': 'steel', 'fairy-feather': 'fairy',
};

// ---------------------------------------------------------------------------
// Abilities (Attacker)
// ---------------------------------------------------------------------------
const ABILITIES = [
  { value: 'none',           label: { es: 'Sin habilidad',             en: 'No Ability' } },
  { value: 'technician',    label: { es: 'Técnico (≤60BP ×1.5)',       en: 'Technician (≤60BP ×1.5)' } },
  { value: 'adaptability',  label: { es: 'Adaptación (STAB →×2)',      en: 'Adaptability (STAB →×2)' } },
  { value: 'huge-power',    label: { es: 'Potencia Enorme (Atq ×2)',   en: 'Huge Power (Atk ×2)' } },
  { value: 'pure-power',    label: { es: 'Poder Puro (Atq ×2)',        en: 'Pure Power (Atk ×2)' } },
  { value: 'hustle',        label: { es: 'Entusiasmo (Fís. ×1.5)',     en: 'Hustle (Phys. ×1.5)' } },
  { value: 'guts',          label: { es: 'Agallas (Statado ×1.5)',     en: 'Guts (Statused ×1.5)' } },
  { value: 'protean',       label: { es: 'Mutatipo (STAB siempre)',    en: 'Protean (Always STAB)' } },
  { value: 'libero',        label: { es: 'Libero (STAB siempre)',      en: 'Libero (Always STAB)' } },
  { value: 'tough-claws',   label: { es: 'Garra Dura (Contacto ×1.3)', en: 'Tough Claws (Contact ×1.3)' } },
  { value: 'iron-fist',     label: { es: 'Puño Férreo (Puño ×1.2)',   en: 'Iron Fist (Punch ×1.2)' } },
  { value: 'strong-jaw',    label: { es: 'Mandíbula Fuerte (Mordida ×1.5)', en: 'Strong Jaw (Bite ×1.5)' } },
  { value: 'mega-launcher', label: { es: 'Lanzador (Pulso ×1.5)',      en: 'Mega Launcher (Pulse ×1.5)' } },
  { value: 'sheer-force',   label: { es: 'Potencia Bruta (Efecto ×1.3)', en: 'Sheer Force (Effect ×1.3)' } },
  { value: 'sand-force',    label: { es: 'Potencia Arena (×1.3 en arena)', en: 'Sand Force (×1.3 in sand)' } },
  { value: 'pixilate',      label: { es: 'Pixilatipo (Normal→Hada ×1.2)', en: 'Pixilate (Normal→Fairy ×1.2)' } },
  { value: 'refrigerate',   label: { es: 'Criogénesis (Normal→Hielo ×1.2)', en: 'Refrigerate (Normal→Ice ×1.2)' } },
  { value: 'aerilate',      label: { es: 'Aerotipo (Normal→Volador ×1.2)', en: 'Aerilate (Normal→Flying ×1.2)' } },
  { value: 'galvanize',     label: { es: 'Galvanitatipo (Normal→Eléc. ×1.2)', en: 'Galvanize (Normal→Electric ×1.2)' } },
  { value: 'torrent',       label: { es: 'Torrente (Agua ×1.5 HP bajo)', en: 'Torrent (Water ×1.5 low HP)' } },
  { value: 'blaze',         label: { es: 'Llamarada (Fuego ×1.5 HP bajo)', en: 'Blaze (Fire ×1.5 low HP)' } },
  { value: 'overgrow',      label: { es: 'Espesura (Planta ×1.5 HP bajo)', en: 'Overgrow (Grass ×1.5 low HP)' } },
  { value: 'swarm',         label: { es: 'Enjambre (Bicho ×1.5 HP bajo)', en: 'Swarm (Bug ×1.5 low HP)' } },
  { value: 'analytic',      label: { es: 'Analítico (último →×1.3)',   en: 'Analytic (Last to move ×1.3)' } },
  { value: 'sniper',        label: { es: 'Francotirador (Crítico ×2.25)', en: 'Sniper (Crit ×2.25)' } },
  { value: 'stakeout',      label: { es: 'Acecho (Cambio ×2)',         en: 'Stakeout (Switch ×2)' } },
  // Gen 8-9 additions
  { value: 'gorilla-tactics', label: { es: 'Tácticas Gorila (Fís. ×1.5)', en: 'Gorilla Tactics (Phys. ×1.5)' } },
  { value: 'transistor',    label: { es: 'Transistor (Eléc. ×1.5)',    en: 'Transistor (Elec. ×1.5)' } },
  { value: 'dragons-maw',   label: { es: 'Fauces Dragón (Dragón ×1.5)', en: "Dragon's Maw (Dragon ×1.5)" } },
  { value: 'steelworker',   label: { es: 'Siderurgia (Acero ×1.5)',    en: 'Steelworker (Steel ×1.5)' } },
  { value: 'rocky-payload', label: { es: 'Carga Rocosa (Roca ×1.5)',   en: 'Rocky Payload (Rock ×1.5)' } },
  { value: 'steely-spirit', label: { es: 'Ánimo Acerado (Acero ×1.5)', en: 'Steely Spirit (Steel ×1.5)' } },
  { value: 'neuroforce',    label: { es: 'Neurofuerza (SE ×1.25)',      en: 'Neuroforce (SE ×1.25)' } },
  { value: 'tinted-lens',   label: { es: 'Lente Tintada (0.5× →1×)',  en: 'Tinted Lens (0.5× →1×)' } },
  { value: 'supreme-overlord', label: { es: 'Supremacía (+10% por KO aliado)', en: 'Supreme Overlord (+10% per fainted ally)' } },
  { value: 'mold-breaker',  label: { es: 'Rompemoldes (ignora hab. def.)', en: 'Mold Breaker (ignore def. ability)' } },
  { value: 'turboblaze',    label: { es: 'Turbocombustión (ignora hab. def.)', en: 'Turboblaze (ignore def. ability)' } },
  { value: 'teravolt',      label: { es: 'Teravoltio (ignora hab. def.)', en: 'Teravolt (ignore def. ability)' } },
];

// ---------------------------------------------------------------------------
// Defender Abilities
// ---------------------------------------------------------------------------
const DEFENDER_ABILITIES = [
  { value: 'none',           label: { es: 'Sin habilidad',               en: 'No Ability' } },
  { value: 'multiscale',     label: { es: 'Multiscala (HP lleno ×0.5)',  en: 'Multiscale (Full HP ×0.5)' } },
  { value: 'shadow-shield',  label: { es: 'Escudo Sombra (HP lleno ×0.5)', en: 'Shadow Shield (Full HP ×0.5)' } },
  { value: 'filter',         label: { es: 'Filtro (SE ×0.75)',           en: 'Filter (SE ×0.75)' } },
  { value: 'solid-rock',     label: { es: 'Roca Sólida (SE ×0.75)',      en: 'Solid Rock (SE ×0.75)' } },
  { value: 'prism-armor',    label: { es: 'Armadura Prisma (SE ×0.75)', en: 'Prism Armor (SE ×0.75)' } },
  { value: 'thick-fat',      label: { es: 'Grueso Gordo (Fuego/Hielo ×0.5)', en: 'Thick Fat (Fire/Ice ×0.5)' } },
  { value: 'fluffy',         label: { es: 'Mullido (Contacto ×2, Fuego ×2)', en: 'Fluffy (Contact ×2, Fire ×2)' } },
  { value: 'fur-coat',       label: { es: 'Pelaje (Fís. ×0.5)',          en: 'Fur Coat (Phys. ×0.5)' } },
  { value: 'ice-scales',     label: { es: 'Escamas Hielo (Esp. ×0.5)',  en: 'Ice Scales (Spec. ×0.5)' } },
  { value: 'punk-rock',      label: { es: 'Punk Rock (Sonido recib. ×0.5)', en: 'Punk Rock (Sound recv. ×0.5)' } },
  { value: 'heatproof',      label: { es: 'Ignífugo (Fuego ×0.5)',       en: 'Heatproof (Fire ×0.5)' } },
  { value: 'wonder-guard',   label: { es: 'Guardia Mágica (solo SE)',    en: 'Wonder Guard (Only SE)' } },
  { value: 'flash-fire',     label: { es: 'Absorbe Fuego (Fuego ×0)',    en: 'Flash Fire (Fire ×0)' } },
  { value: 'water-absorb',   label: { es: 'Absorbe Agua (Agua ×0)',      en: 'Water Absorb (Water ×0)' } },
  { value: 'dry-skin',       label: { es: 'Piel Seca (Agua ×0, Fuego ×1.25)', en: 'Dry Skin (Water ×0, Fire ×1.25)' } },
  { value: 'storm-drain',    label: { es: 'Drenalluvia (Agua ×0)',       en: 'Storm Drain (Water ×0)' } },
  { value: 'volt-absorb',    label: { es: 'Absorbe Elec. (Elec. ×0)',   en: 'Volt Absorb (Elec. ×0)' } },
  { value: 'motor-drive',    label: { es: 'Motor Eléctrico (Elec. ×0)', en: 'Motor Drive (Elec. ×0)' } },
  { value: 'lightning-rod',  label: { es: 'Pararrayos (Elec. ×0)',       en: 'Lightning Rod (Elec. ×0)' } },
  { value: 'levitate',       label: { es: 'Levitación (Tierra ×0)',      en: 'Levitate (Ground ×0)' } },
  { value: 'sap-sipper',     label: { es: 'Come Hierba (Planta ×0)',     en: 'Sap Sipper (Grass ×0)' } },
  { value: 'earth-eater',    label: { es: 'Come Tierra (Tierra ×0)',     en: 'Earth Eater (Ground ×0)' } },
  { value: 'well-baked-body',label: { es: 'Cuerpo Cocido (Fuego ×0)',    en: 'Well-Baked Body (Fire ×0)' } },
  { value: 'wind-rider',     label: { es: 'Jinete del Viento (Viento ×0)', en: 'Wind Rider (Wind ×0)' } },
];

// ---------------------------------------------------------------------------
// Defender Items
// ---------------------------------------------------------------------------
const DEFENDER_ITEMS = [
  { value: 'none',          label: { es: 'Sin objeto',                    en: 'No Item' } },
  { value: 'assault-vest',  label: { es: 'Chaleco Asalto (Sp.Def ×1.5)', en: 'Assault Vest (Sp.Def ×1.5)' } },
  { value: 'eviolite',      label: { es: 'Eviolita (Def/Sp.Def ×1.5)',   en: 'Eviolite (Def/Sp.Def ×1.5)' } },
  { value: 'rocky-helmet',  label: { es: 'Casco Férreo (daño contacto)', en: 'Rocky Helmet (contact dmg)' } },
];

// ---------------------------------------------------------------------------
// All 18 types list
// ---------------------------------------------------------------------------
const ALL_TYPES = ['normal','fire','water','electric','grass','ice','fighting','poison','ground','flying','psychic','bug','rock','ghost','dragon','dark','steel','fairy'];

// ---------------------------------------------------------------------------
// Z-Move BP table
// ---------------------------------------------------------------------------
function getZMoveBP(basePower) {
  if (basePower <= 55)  return 100;
  if (basePower <= 65)  return 120;
  if (basePower <= 75)  return 140;
  if (basePower <= 85)  return 160;
  if (basePower <= 95)  return 175;
  if (basePower <= 100) return 180;
  if (basePower <= 110) return 185;
  if (basePower <= 125) return 190;
  if (basePower <= 135) return 195;
  return 200;
}

// ---------------------------------------------------------------------------
// Dynamax BP table
// ---------------------------------------------------------------------------
function getDynamaxBP(basePower) {
  if (basePower <= 65)  return 90;
  if (basePower <= 75)  return 100;
  if (basePower <= 85)  return 110;
  if (basePower <= 95)  return 120;
  if (basePower <= 109) return 130;
  if (basePower <= 119) return 140;
  if (basePower <= 129) return 150;
  if (basePower <= 139) return 160;
  return 160;
}

// ---------------------------------------------------------------------------
// Format forme name nicely
// ---------------------------------------------------------------------------
function formatFormeName(name) {
  if (!name) return name;
  const replacements = {
    'mega-x': 'Mega X', 'mega-y': 'Mega Y', 'mega': 'Mega',
    'alola': 'Alolan', 'galar': 'Galarian', 'hisui': 'Hisuian',
    'paldea': 'Paldean', 'gmax': 'Gigantamax', 'totem': 'Totem',
  };
  const parts = name.split('-');
  const pokemonName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  const suffix = parts.slice(1).join('-');
  if (!suffix) return pokemonName;
  for (const [key, val] of Object.entries(replacements)) {
    if (suffix === key || suffix.startsWith(key + '-') || suffix.endsWith('-' + key)) {
      const remaining = suffix.replace(key, '').replace(/-/g, ' ').trim();
      return remaining
        ? `${val} ${pokemonName} ${remaining.charAt(0).toUpperCase() + remaining.slice(1)}`
        : `${val} ${pokemonName}`;
    }
  }
  return name.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Calculate type effectiveness
// ---------------------------------------------------------------------------
function getTypeEffectiveness(moveType, defenderTypes) {
  let multiplier = 1;
  defenderTypes.forEach(defType => {
    const effectiveness = TYPE_EFFECTIVENESS[moveType]?.[defType];
    if (effectiveness !== undefined) {
      multiplier *= effectiveness;
    }
  });
  return multiplier;
}

// Damage calculation function (Gen 5+ formula)
function calculateDamage({
  level,
  power: rawPower,
  attack,
  defense,
  attackStage,
  defenseStage,
  stab,
  typeEffectiveness,
  critical,
  weather,
  terrain,
  moveType,
  isPhysical,
  burned,
  screens,           // reflect/light screen
  ability = 'none',
  item = 'none',
  targets,           // 1 for singles, 0.75 for spread in doubles
  lowHPAttacker = false,
  isContact = false,
  hasPunchFlag = false,
  hasBiteFlag = false,
  hasPulseFlag = false,
  hasSoundFlag = false,
  hasSecondaryEffect = false,
  // Defender-side
  defenderAbility = 'none',
  defenderItem = 'none',
  defenderAtFullHP = true,
  alliedFainted = 0,  // for Supreme Overlord
  // New gen/mechanic params
  generation = 9,
  parentalBond = false,
  isZMove = false,
  isDynamax = false,
  isTera = false,
  teraType = 'none',
  attackerOriginalTypes = [],
  defenderTeraActive = false,
  defenderTeraType = 'none',
  defenderOriginalTypes = [],
}) {
  if (rawPower === 0) return { min: 0, max: 0, rolls: Array(16).fill(0), immune: false, totalMin: 0, totalMax: 0, totalRolls: Array(16).fill(0) };

  // Ignore defender ability with Mold Breaker / Turboblaze / Teravolt
  const ignoresDefAbility = ['mold-breaker', 'turboblaze', 'teravolt'].includes(ability);
  const effectiveDefAbility = ignoresDefAbility ? 'none' : defenderAbility;

  // Immunity check (defender ability)
  const IMMUNITIES = {
    'flash-fire':    ['fire'],
    'water-absorb':  ['water'],
    'dry-skin':      ['water'],
    'storm-drain':   ['water'],
    'volt-absorb':   ['electric'],
    'motor-drive':   ['electric'],
    'lightning-rod': ['electric'],
    'levitate':      ['ground'],
    'sap-sipper':    ['grass'],
    'earth-eater':   ['ground'],
    'well-baked-body': ['fire'],
  };

  // Override move BP for Z-Moves (Gen 7+)
  let power = rawPower;
  if (isZMove && generation >= 7) {
    power = getZMoveBP(rawPower);
  }
  // Override move BP for Dynamax (Gen 8)
  if (isDynamax && generation === 8) {
    power = getDynamaxBP(rawPower);
  }

  // Tera STAB calculation (Gen 9 only)
  let effectiveSTAB = stab;
  let teraSTABMod = 1;
  if (isTera && generation === 9 && teraType !== 'none') {
    // Only Tera type matters for STAB
    if (moveType === teraType) {
      const inOriginal = attackerOriginalTypes.includes(teraType);
      teraSTABMod = inOriginal ? 2.0 : 1.5;
      effectiveSTAB = false; // handled by teraSTABMod below
    } else {
      // Move type differs from Tera type: it still keeps its original STAB
      // if the move type matches one of the attacker's original types.
      effectiveSTAB = stab;
    }
  }

  // Defender Tera: override defender types for type effectiveness
  let effectiveDefenderTypes = defenderOriginalTypes;
  if (defenderTeraActive && generation === 9 && defenderTeraType !== 'none') {
    effectiveDefenderTypes = [defenderTeraType];
  }
  // Recalculate typeEffectiveness if defender tera active
  let effectiveTypeEff2 = typeEffectiveness;
  if (defenderTeraActive && generation === 9 && defenderTeraType !== 'none' && effectiveDefenderTypes.length > 0) {
    effectiveTypeEff2 = getTypeEffectiveness(moveType, effectiveDefenderTypes);
  }

  // -ate abilities convert Normal → another type and give ×1.2/1.3 bonus (gen 6 = 1.3, gen 7+ = 1.2)
  const ATE_MAP = { pixilate: 'fairy', refrigerate: 'ice', aerilate: 'flying', galvanize: 'electric' };
  let effectiveMoveType = moveType;
  let ateMod = 1;
  if (ATE_MAP[ability] && moveType === 'normal') {
    effectiveMoveType = ATE_MAP[ability];
    ateMod = generation === 6 ? 1.3 : 1.2;
  }

  // -ate abilities change the move's type, so recompute effectiveness with the new type
  let effectiveTypeEff = effectiveTypeEff2;
  if (effectiveMoveType !== moveType) {
    effectiveTypeEff = getTypeEffectiveness(effectiveMoveType, effectiveDefenderTypes);
  }
  // Tinted Lens: resisted moves (0.5×) become neutral (1×)
  if (ability === 'tinted-lens' && effectiveTypeEff < 1 && effectiveTypeEff > 0) {
    effectiveTypeEff = 1;
  }

  // Wonder Guard: only super-effective moves deal damage
  if (effectiveDefAbility === 'wonder-guard' && effectiveTypeEff <= 1) {
    return { min: 0, max: 0, rolls: Array(16).fill(0), immune: true, totalMin: 0, totalMax: 0, totalRolls: Array(16).fill(0) };
  }

  // Immunity from defender ability
  if (IMMUNITIES[effectiveDefAbility]?.includes(effectiveMoveType)) {
    return { min: 0, max: 0, rolls: Array(16).fill(0), immune: true, totalMin: 0, totalMax: 0, totalRolls: Array(16).fill(0) };
  }

  // Effective attack/defense after stat stages
  const attackMod  = STAT_STAGES[String(attackStage)]  || 1;
  const defenseMod = STAT_STAGES[String(defenseStage)] || 1;

  let effectiveAttack  = Math.floor(attack  * attackMod);
  let effectiveDefense = Math.floor(defense * defenseMod);

  // Huge Power / Pure Power double the attack stat
  if ((ability === 'huge-power' || ability === 'pure-power') && isPhysical) {
    effectiveAttack = Math.floor(effectiveAttack * 2);
  }

  // Defender item modifying defense
  if (defenderItem === 'assault-vest' && !isPhysical) {
    effectiveDefense = Math.floor(effectiveDefense * 1.5);
  }
  if (defenderItem === 'eviolite') {
    effectiveDefense = Math.floor(effectiveDefense * 1.5);
  }

  // Base damage (Gen 5+ formula with proper floor chain)
  let baseDamage = Math.floor(
    Math.floor(Math.floor(2 * level / 5 + 2) * power * effectiveAttack / effectiveDefense) / 50 + 2
  );

  // Build the modifier chain
  let modifier = 1;

  // Targets (spread moves in doubles)
  modifier *= targets;

  // Weather
  if (weather === 'sun') {
    if (effectiveMoveType === 'fire')  modifier *= 1.5;
    if (effectiveMoveType === 'water') modifier *= 0.5;
  } else if (weather === 'rain') {
    if (effectiveMoveType === 'water') modifier *= 1.5;
    if (effectiveMoveType === 'fire')  modifier *= 0.5;
  }

  // Terrain
  if (terrain === 'electric' && effectiveMoveType === 'electric') modifier *= 1.3;
  if (terrain === 'grassy'   && effectiveMoveType === 'grass')    modifier *= 1.3;
  if (terrain === 'psychic'  && effectiveMoveType === 'psychic')  modifier *= 1.3;

  // Critical hit multiplier — gen-dependent. Gen 1-5 = 2.0, Gen 6+ = 1.5
  // Sniper always doubles the crit multiplier
  if (critical) {
    const critMult = generation <= 5 ? 2.0 : 1.5;
    modifier *= (ability === 'sniper' ? critMult * 1.5 : critMult);
  }

  // STAB (Adaptability → 2.0; Protean/Libero → always 1.5; Tera handled separately)
  const hasSTAB = effectiveSTAB || ability === 'protean' || ability === 'libero';
  if (hasSTAB) modifier *= (ability === 'adaptability' ? 2.0 : 1.5);

  // Tera STAB (Gen 9)
  if (teraSTABMod > 1) modifier *= teraSTABMod;

  // -ate bonus
  modifier *= ateMod;

  // Type effectiveness (with tinted lens adjustment applied)
  modifier *= effectiveTypeEff;

  // Burn (halves physical, but Guts ignores burn penalty)
  if (burned && isPhysical && ability !== 'guts') modifier *= 0.5;

  // Guts — 1.5× attack when statused
  if (ability === 'guts' && burned && isPhysical) modifier *= 1.5;

  // Hustle (physical only)
  if (ability === 'hustle' && isPhysical) modifier *= 1.5;

  // Screens — halved, but crits bypass screens; Z-Moves bypass screens
  if (screens && !critical && !isZMove) modifier *= 0.5;

  // Technician (base power ≤ 60)
  if (ability === 'technician' && power <= 60) modifier *= 1.5;

  // Tough Claws (contact moves)
  if (ability === 'tough-claws' && isContact) modifier *= 1.3;

  // Iron Fist (punch moves)
  if (ability === 'iron-fist' && hasPunchFlag) modifier *= 1.2;

  // Strong Jaw (bite moves)
  if (ability === 'strong-jaw' && hasBiteFlag) modifier *= 1.5;

  // Mega Launcher (pulse/aura moves)
  if (ability === 'mega-launcher' && hasPulseFlag) modifier *= 1.5;

  // Sheer Force (moves with secondary effects)
  if (ability === 'sheer-force' && hasSecondaryEffect) modifier *= 1.3;

  // Sand Force (Rock / Steel / Ground in Sandstorm)
  if (ability === 'sand-force' && weather === 'sand' && ['rock','steel','ground'].includes(effectiveMoveType)) modifier *= 1.3;

  // Torrent / Blaze / Overgrow / Swarm at low HP
  if (lowHPAttacker) {
    if (ability === 'torrent'  && effectiveMoveType === 'water') modifier *= 1.5;
    if (ability === 'blaze'    && effectiveMoveType === 'fire')  modifier *= 1.5;
    if (ability === 'overgrow' && effectiveMoveType === 'grass') modifier *= 1.5;
    if (ability === 'swarm'    && effectiveMoveType === 'bug')   modifier *= 1.5;
  }

  // Analytic (simplified: always active)
  if (ability === 'analytic') modifier *= 1.3;

  // Stakeout (opponent just switched)
  if (ability === 'stakeout') modifier *= 2;

  // Gorilla Tactics (physical ×1.5)
  if (ability === 'gorilla-tactics' && isPhysical) modifier *= 1.5;

  // Type-specific boosts Gen 8-9
  if (ability === 'transistor'    && effectiveMoveType === 'electric') modifier *= 1.5;
  if (ability === 'dragons-maw'   && effectiveMoveType === 'dragon')   modifier *= 1.5;
  if (ability === 'steelworker'   && effectiveMoveType === 'steel')    modifier *= 1.5;
  if (ability === 'steely-spirit' && effectiveMoveType === 'steel')    modifier *= 1.5;
  if (ability === 'rocky-payload' && effectiveMoveType === 'rock')     modifier *= 1.5;

  // Neuroforce (super-effective ×1.25)
  if (ability === 'neuroforce' && effectiveTypeEff > 1) modifier *= 1.25;

  // Supreme Overlord (×1.1 per fainted ally, max 5)
  if (ability === 'supreme-overlord' && alliedFainted > 0) {
    modifier *= 1 + Math.min(5, alliedFainted) * 0.1;
  }

  // ---- Held items ----
  if (item === 'life-orb')     modifier *= 1.3;
  else if (item === 'choice-band'  && isPhysical)  modifier *= 1.5;
  else if (item === 'choice-specs' && !isPhysical) modifier *= 1.5;
  else if (item === 'muscle-band'  && isPhysical)  modifier *= 1.1;
  else if (item === 'wise-glasses' && !isPhysical) modifier *= 1.1;
  else if (item === 'expert-belt' && effectiveTypeEff > 1) modifier *= 1.2;
  else if (ITEM_TYPE_BOOST[item] && ITEM_TYPE_BOOST[item] === effectiveMoveType) modifier *= 1.2;

  // ---- Defender abilities (damage reduction) ----
  if (!critical) {
    // Multiscale / Shadow Shield — only at full HP
    if ((effectiveDefAbility === 'multiscale' || effectiveDefAbility === 'shadow-shield') && defenderAtFullHP) {
      modifier *= 0.5;
    }
    // Filter / Solid Rock / Prism Armor — SE damage ×0.75
    if (['filter','solid-rock','prism-armor'].includes(effectiveDefAbility) && effectiveTypeEff > 1) {
      modifier *= 0.75;
    }
  }
  // Thick Fat — Fire/Ice ×0.5
  if (effectiveDefAbility === 'thick-fat' && ['fire','ice'].includes(effectiveMoveType)) {
    modifier *= 0.5;
  }
  // Fluffy — contact ×2, fire ×2
  if (effectiveDefAbility === 'fluffy') {
    if (isContact) modifier *= 2;
    if (effectiveMoveType === 'fire') modifier *= 2;
  }
  // Fur Coat — physical ×0.5
  if (effectiveDefAbility === 'fur-coat' && isPhysical) modifier *= 0.5;
  // Ice Scales — special ×0.5
  if (effectiveDefAbility === 'ice-scales' && !isPhysical) modifier *= 0.5;
  // Punk Rock — sound ×0.5 received
  if (effectiveDefAbility === 'punk-rock' && hasSoundFlag) modifier *= 0.5;
  // Heatproof — fire ×0.5
  if (effectiveDefAbility === 'heatproof' && effectiveMoveType === 'fire') modifier *= 0.5;
  // Dry Skin — fire ×1.25
  if (effectiveDefAbility === 'dry-skin' && effectiveMoveType === 'fire') modifier *= 1.25;

  // All 16 rolls (85/100 → 100/100, one per step)
  const rolls = [];
  for (let i = 0; i < 16; i++) {
    rolls.push(Math.max(1, Math.floor(baseDamage * modifier * (85 + i) / 100)));
  }

  // Parental Bond: second hit is 0.25× (Gen 7+) or 0.5× (Gen 6)
  let hit2Rolls = null, hit2Min = null, hit2Max = null;
  let totalRolls = rolls, totalMin = rolls[0], totalMax = rolls[15];
  if (parentalBond) {
    const secondHitMult = generation <= 6 ? 0.5 : 0.25;
    hit2Rolls = rolls.map(r => Math.max(1, Math.floor(r * secondHitMult)));
    hit2Min = hit2Rolls[0];
    hit2Max = hit2Rolls[15];
    totalRolls = rolls.map((r, i) => r + hit2Rolls[i]);
    totalMin = totalRolls[0];
    totalMax = totalRolls[15];
  }

  return {
    min: rolls[0], max: rolls[15], rolls, immune: false,
    totalMin, totalMax, totalRolls,
    parentalBond,
    hit2Min, hit2Max, hit2Rolls,
  };
}

// Pokemon Selector Component
function PokemonSelector({ pokemon, onSelect, label, side }) {
  const { language, addToCache, getFromCache } = useApp();
  const { getPokemonList, getPokemon } = usePokemon();
  const [search, setSearch] = useState('');
  const [pokemonList, setPokemonList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [varieties, setVarieties] = useState([]);
  const [selectedForme, setSelectedForme] = useState('');

  useEffect(() => {
    const fetchList = async () => {
      const cached = getFromCache('pokemon-list-calc');
      if (cached) {
        setPokemonList(cached);
        return;
      }
      const list = await getPokemonList();
      setPokemonList(list);
      addToCache('pokemon-list-calc', list);
    };
    fetchList();
  }, [getPokemonList, addToCache, getFromCache]);

  const filteredList = useMemo(() => {
    if (!search) return pokemonList.slice(0, 20);
    const query = search.toLowerCase();
    return pokemonList.filter(p =>
      p.name.includes(query) || String(p.id).startsWith(query)
    ).slice(0, 20);
  }, [pokemonList, search]);

  const handleSelect = async (p) => {
    setLoading(true);
    setVarieties([]);
    setSelectedForme('');
    const data = await getPokemon(p.id);
    onSelect(data);
    setShowDropdown(false);
    setSearch('');
    // Fetch species for varieties
    try {
      const speciesCacheKey = `species-${p.name}`;
      let speciesData = getFromCache(speciesCacheKey);
      if (!speciesData) {
        const res = await fetch(`https://pokeapi.co/api/v2/pokemon-species/${p.name}`);
        if (res.ok) {
          speciesData = await res.json();
          addToCache(speciesCacheKey, speciesData);
        }
      }
      if (speciesData?.varieties && speciesData.varieties.length > 1) {
        setVarieties(speciesData.varieties);
        setSelectedForme(speciesData.varieties.find(v => v.is_default)?.pokemon?.name || speciesData.varieties[0]?.pokemon?.name || '');
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const handleFormeChange = async (formeName) => {
    setSelectedForme(formeName);
    setLoading(true);
    try {
      const cacheKey = `pokemon-name-${formeName}`;
      let formeData = getFromCache(cacheKey);
      if (!formeData) {
        const res = await fetch(`https://pokeapi.co/api/v2/pokemon/${formeName}`);
        if (res.ok) {
          formeData = await res.json();
          addToCache(cacheKey, formeData);
        }
      }
      if (formeData) onSelect(formeData);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  return (
    <div className="relative">
      <label className="text-sm font-semibold text-muted-foreground mb-1 block">{label}</label>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="w-full bg-background border border-input rounded-lg px-3 py-2 text-left flex items-center justify-between hover:border-violet-500/50 transition-colors"
        data-testid={`pokemon-selector-${side}`}
      >
        {pokemon ? (
          <div className="flex items-center gap-2">
            <img
              src={pokemon.sprites?.front_default}
              alt={pokemon.name}
              className="w-8 h-8"
            />
            <span className="capitalize font-medium">{pokemon.name.replace(/-/g,' ')}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">
            {language === 'es' ? 'Seleccionar Pokémon' : 'Select Pokémon'}
          </span>
        )}
        <ChevronDown className={cn('w-4 h-4 transition-transform', showDropdown && 'rotate-180')} />
      </button>

      {/* Forme dropdown */}
      {varieties.length > 1 && (
        <div className="mt-1">
          <label className="text-xs text-muted-foreground block mb-1">
            {language === 'es' ? 'Forma' : 'Forme'}
          </label>
          <select
            value={selectedForme}
            onChange={e => handleFormeChange(e.target.value)}
            className="w-full bg-background border border-input rounded-lg px-2 py-1.5 text-xs"
          >
            {varieties.map(v => (
              <option key={v.pokemon.name} value={v.pokemon.name}>
                {formatFormeName(v.pokemon.name)}
              </option>
            ))}
          </select>
        </div>
      )}

      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={language === 'es' ? 'Buscar...' : 'Search...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredList.map(p => (
              <button
                key={p.id}
                onClick={() => handleSelect(p)}
                className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2"
              >
                <img
                  src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${p.id}.png`}
                  alt={p.name}
                  className="w-6 h-6"
                />
                <span className="capitalize text-sm">#{p.id} {p.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {loading && (
        <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-lg">
          <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

// Move Selector Component — loads ALL moves with search filter
function MoveSelector({ pokemon, selectedMove, onSelect }) {
  const { language, addToCache, getFromCache } = useApp();
  const { getMove } = usePokemon();
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [moveSearch, setMoveSearch] = useState('');

  useEffect(() => {
    if (!pokemon?.moves) { setMoves([]); return; }
    const fetchMoves = async () => {
      setLoading(true);
      // Fetch ALL moves in batches of 10 to avoid rate-limiting
      const allMoves = pokemon.moves;
      const BATCH = 10;
      const results = [];
      for (let i = 0; i < allMoves.length; i += BATCH) {
        const batch = allMoves.slice(i, i + BATCH);
        const batchData = await Promise.all(
          batch.map(async (m) => {
            const cached = getFromCache(`move-${m.move.name}`);
            if (cached) return cached;
            const data = await getMove(m.move.name);
            if (data) addToCache(`move-${m.move.name}`, data);
            return data;
          })
        );
        results.push(...batchData.filter(Boolean).filter(d => d.power > 0));
      }
      setMoves(results.sort((a, b) => a.name.localeCompare(b.name)));
      setLoading(false);
    };
    fetchMoves();
  }, [pokemon, getMove, addToCache, getFromCache]);

  const filteredMoves = useMemo(() => {
    if (!moveSearch) return moves;
    const q = moveSearch.toLowerCase();
    return moves.filter(m =>
      m.name.includes(q) || m.type?.name?.includes(q)
    );
  }, [moves, moveSearch]);

  return (
    <div className="relative">
      <label className="text-sm font-semibold text-muted-foreground mb-1 block">
        {language === 'es' ? 'Movimiento' : 'Move'}
        {loading && <span className="ml-2 text-xs text-violet-500 animate-pulse">{language === 'es' ? 'cargando...' : 'loading...'}</span>}
      </label>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={!pokemon}
        className="w-full bg-background border border-input rounded-lg px-3 py-2 text-left flex items-center justify-between hover:border-violet-500/50 transition-colors disabled:opacity-50"
        data-testid="move-selector"
      >
        {selectedMove ? (
          <div className="flex items-center gap-2">
            <span className={cn(
              'px-2 py-0.5 rounded text-xs font-semibold uppercase',
              TYPE_COLORS[selectedMove.type?.name] || 'bg-gray-400',
              ['electric','ice','flying','fairy','steel','normal'].includes(selectedMove.type?.name) ? 'text-gray-900' : 'text-white'
            )}>
              {TYPE_NAMES[selectedMove.type?.name]?.[language] || selectedMove.type?.name}
            </span>
            <span className="capitalize font-medium">{selectedMove.name.replace(/-/g, ' ')}</span>
            <span className="text-muted-foreground text-sm">({selectedMove.power} BP)</span>
            {/* Flag icons */}
            <span className="text-[10px] text-muted-foreground">
              {selectedMove.flags?.some(f => f.name === 'contact') && '👊'}
              {selectedMove.flags?.some(f => f.name === 'punch') && '🥊'}
              {selectedMove.flags?.some(f => f.name === 'bite') && '🦷'}
              {selectedMove.flags?.some(f => f.name === 'pulse') && '🔵'}
              {selectedMove.flags?.some(f => f.name === 'sound') && '🔊'}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground">
            {language === 'es' ? 'Seleccionar movimiento' : 'Select move'}
          </span>
        )}
        <ChevronDown className={cn('w-4 h-4 transition-transform', showDropdown && 'rotate-180')} />
      </button>

      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="p-2 border-b border-border shrink-0">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={moveSearch}
                onChange={e => setMoveSearch(e.target.value)}
                placeholder={language === 'es' ? 'Buscar movimiento...' : 'Search move...'}
                className="pl-7 h-7 text-xs"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto">
            {filteredMoves.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                {loading ? (language === 'es' ? 'Cargando movimientos...' : 'Loading moves...') : (language === 'es' ? 'Sin resultados' : 'No results')}
              </div>
            )}
            {filteredMoves.map(move => (
              <button
                key={move.id}
                onClick={() => { onSelect(move); setShowDropdown(false); setMoveSearch(''); trackEvent('move_selected', { moveName: move.name, moveType: move.type?.name }); }}
                className="w-full px-3 py-1.5 text-left hover:bg-muted flex items-center gap-2"
              >
                <span className={cn(
                  'px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase min-w-[40px] text-center shrink-0',
                  TYPE_COLORS[move.type?.name] || 'bg-gray-400',
                  ['electric','ice','flying','fairy','steel','normal'].includes(move.type?.name) ? 'text-gray-900' : 'text-white'
                )}>
                  {move.type?.name?.slice(0, 4)}
                </span>
                <span className="capitalize text-xs flex-1">{move.name.replace(/-/g, ' ')}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{move.power} BP</span>
                <span className="text-[10px] text-muted-foreground shrink-0">
                  {move.damage_class?.name === 'physical' ? '⚔' : '✦'}
                </span>
                {/* Flag icons */}
                <span className="text-[10px] shrink-0">
                  {move.flags?.some(f => f.name === 'contact') && '👊'}
                  {move.flags?.some(f => f.name === 'punch') && '🥊'}
                  {move.flags?.some(f => f.name === 'bite') && '🦷'}
                  {move.flags?.some(f => f.name === 'pulse') && '🔵'}
                  {move.flags?.some(f => f.name === 'sound') && '🔊'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Input Component
function StatInput({ label, value, onChange, min = 1, max = 999 }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(Math.max(min, Math.min(max, parseInt(e.target.value) || min)))}
        className="h-8 text-center"
        min={min}
        max={max}
      />
    </div>
  );
}

/** Build a damage description string */
function buildDamageString({
  attacker, defender, selectedMove,
  attackerEV, attackerNature, attackerItem, attackerAbility,
  defenderHPEV, defenderDefEV, defenderNature, defenderItem, defenderAbility,
  defenderHP, result, language,
}) {
  if (!attacker || !defender || !selectedMove || !result || result.max === 0) return null;

  const isPhysical = selectedMove.damage_class?.name === 'physical';
  const atkStatKey = isPhysical ? 'attack' : 'special-attack';
  const defStatKey = isPhysical ? 'defense' : 'special-defense';

  const atkNature = NATURES[attackerNature] || {};
  const defNature = NATURES[defenderNature] || {};

  const atkNatureTag = atkNature.boost === atkStatKey ? '+' : atkNature.drop === atkStatKey ? '-' : '';
  const defNatureTag = defNature.boost === defStatKey ? '+' : defNature.drop === defStatKey ? '-' : '';

  const statLabel = isPhysical ? 'Atk' : 'SpA';
  const defLabel  = isPhysical ? 'Def' : 'SpD';

  const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  const titleCase = (s) => s ? s.replace(/-/g, ' ').split(' ').map(cap).join(' ') : '';

  const atkParts = [];
  if (attackerEV > 0 || atkNatureTag) atkParts.push(`${attackerEV || 0}${atkNatureTag}`);
  atkParts.push(statLabel);
  if (attackerItem !== 'none') atkParts.push(titleCase(attackerItem));
  if (attackerAbility !== 'none') atkParts.push(titleCase(attackerAbility));
  atkParts.push(cap(attacker.name));
  atkParts.push(titleCase(selectedMove.name));

  const defHPStr = `${defenderHPEV || 0} HP`;
  const defDefStr = `${defenderDefEV || 0}${defNatureTag} ${defLabel}`;
  const defParts = [defHPStr, defDefStr];
  if (defenderAbility !== 'none') defParts.push(titleCase(defenderAbility));
  if (defenderItem !== 'none') defParts.push(titleCase(defenderItem));
  defParts.push(cap(defender.name));

  const minPct = ((result.min / defenderHP) * 100).toFixed(1);
  const maxPct = ((result.max / defenderHP) * 100).toFixed(1);
  const avgRoll = result.rolls ? result.rolls.reduce((a, b) => a + b, 0) / 16 : (result.min + result.max) / 2;
  const hitsToKO = Math.ceil(defenderHP / avgRoll);

  let koNote = '';
  if (parseFloat(minPct) >= 100)       koNote = language === 'es' ? 'OHKO garantizado' : 'guaranteed OHKO';
  else if (parseFloat(maxPct) >= 100)  koNote = language === 'es' ? 'OHKO posible' : 'possible OHKO';
  else if (parseFloat(minPct) * 2 >= 100) koNote = language === 'es' ? '2HKO garantizado' : 'guaranteed 2HKO';
  else if (parseFloat(maxPct) * 2 >= 100) koNote = language === 'es' ? '2HKO posible' : 'possible 2HKO';
  else if (parseFloat(minPct) * 3 >= 100) koNote = language === 'es' ? '3HKO garantizado' : 'guaranteed 3HKO';
  else if (parseFloat(maxPct) * 3 >= 100) koNote = language === 'es' ? '3HKO posible' : 'possible 3HKO';
  else koNote = `${hitsToKO}HKO`;

  return `${atkParts.join(' ')} vs. ${defParts.join(' / ')}: ${result.min}-${result.max} (${minPct} – ${maxPct}%) -- ${koNote}`;
}

// Damage Result Component
function DamageResult({ result, defenderHP, damageStr, immune, hazardDamage, defenderStatus, effectiveHP }) {
  const { language } = useApp();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (damageStr) {
      navigator.clipboard.writeText(damageStr).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  if (!result) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {language === 'es'
          ? 'Selecciona un atacante, defensor y movimiento'
          : 'Select an attacker, defender, and move'}
      </div>
    );
  }

  if (immune || result.max === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-gray-500/10 to-gray-700/10 rounded-xl p-6 text-center"
        data-testid="damage-result"
      >
        <div className="text-2xl font-bold text-muted-foreground mb-1">
          {language === 'es' ? '¡INMUNE!' : 'IMMUNE!'}
        </div>
        <p className="text-xs text-muted-foreground">
          {language === 'es' ? 'La habilidad del defensor bloquea este movimiento.' : "Defender's ability blocks this move."}
        </p>
      </motion.div>
    );
  }

  // Use effective HP (after hazards) for KO calculations
  const hpForKO = effectiveHP || defenderHP;

  const minPercent = ((result.min / defenderHP) * 100).toFixed(1);
  const maxPercent = ((result.max / defenderHP) * 100).toFixed(1);
  const avgRoll = result.rolls ? result.rolls.reduce((a, b) => a + b, 0) / 16 : (result.min + result.max) / 2;
  const avgPercent = ((avgRoll / defenderHP) * 100).toFixed(1);

  // For Parental Bond, use total damage for KO calc
  const totalMin = result.parentalBond ? result.totalMin : result.min;
  const totalMax = result.parentalBond ? result.totalMax : result.max;
  const totalAvg = result.parentalBond && result.totalRolls
    ? result.totalRolls.reduce((a, b) => a + b, 0) / 16
    : avgRoll;
  const hitsToKO = Math.ceil(hpForKO / totalAvg);

  const getColorClass = () => {
    const avg = parseFloat(avgPercent);
    if (avg >= 100) return 'text-green-500';
    if (avg >= 50)  return 'text-yellow-500';
    if (avg >= 25)  return 'text-orange-500';
    return 'text-rose-500';
  };

  const getKOLabel = () => {
    const totalMinPct = (totalMin / hpForKO) * 100;
    const totalMaxPct = (totalMax / hpForKO) * 100;
    let label = '';
    let cls = '';
    if (totalMinPct >= 100)       { label = language === 'es' ? '¡OHKO garantizado!' : 'Guaranteed OHKO!'; cls = 'text-green-500'; }
    else if (totalMaxPct >= 100)  { label = language === 'es' ? 'OHKO posible' : 'Possible OHKO'; cls = 'text-yellow-500'; }
    else if (totalMinPct * 2 >= 100) { label = language === 'es' ? '2HKO garantizado' : 'Guaranteed 2HKO'; cls = 'text-yellow-400'; }
    else if (totalMaxPct * 2 >= 100) { label = language === 'es' ? '2HKO posible' : 'Possible 2HKO'; cls = 'text-orange-400'; }
    else if (totalMinPct * 3 >= 100) { label = language === 'es' ? '3HKO garantizado' : 'Guaranteed 3HKO'; cls = 'text-orange-400'; }
    else { label = `${hitsToKO}HKO`; cls = 'text-muted-foreground'; }
    // Append status note
    if (defenderStatus && defenderStatus !== 'none') {
      const statusNote = {
        poison: language === 'es' ? ' + daño veneno' : ' + poison dmg',
        badly_poisoned: language === 'es' ? ' + daño tóxico' : ' + toxic dmg',
        burn: language === 'es' ? ' + daño quemadura' : ' + burn dmg',
      }[defenderStatus] || '';
      label += statusNote;
    }
    return { label, cls };
  };

  const ko = getKOLabel();

  // Bar fill width for visual (single hit)
  const barMin = Math.min(100, parseFloat(minPercent));
  const barMax = Math.min(100, parseFloat(maxPercent));

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-br from-violet-500/10 to-rose-500/10 rounded-xl p-5 text-center"
      data-testid="damage-result"
    >
      <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
        {language === 'es' ? 'Daño Calculado' : 'Calculated Damage'}
      </h3>

      {/* Hazard info */}
      {hazardDamage > 0 && (
        <div className="text-xs text-amber-500 mb-2">
          ⚠️ {language === 'es' ? 'Tras entrada:' : 'After hazards:'} {hpForKO} HP ({defenderHP - hpForKO} {language === 'es' ? 'daño entrada' : 'hazard dmg'})
        </div>
      )}

      {/* Parental Bond: show both hits */}
      {result.parentalBond ? (
        <div className="space-y-1 mb-3">
          <div className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{language === 'es' ? 'Golpe 1' : 'Hit 1'}:</span> {result.min}–{result.max} HP ({((result.min / defenderHP) * 100).toFixed(1)}%–{((result.max / defenderHP) * 100).toFixed(1)}%)
          </div>
          <div className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{language === 'es' ? 'Golpe 2' : 'Hit 2'}:</span> {result.hit2Min}–{result.hit2Max} HP ({((result.hit2Min / defenderHP) * 100).toFixed(1)}%–{((result.hit2Max / defenderHP) * 100).toFixed(1)}%)
          </div>
          <div className={cn('text-2xl font-bold', getColorClass())}>
            {language === 'es' ? 'Total' : 'Total'}: {((totalMin / defenderHP) * 100).toFixed(1)}%–{((totalMax / defenderHP) * 100).toFixed(1)}%
          </div>
          <div className="text-base text-muted-foreground">
            {totalMin}–{totalMax} HP
          </div>
        </div>
      ) : (
        <>
          <div className={cn('text-4xl font-bold mb-1', getColorClass())}>
            {minPercent}% – {maxPercent}%
          </div>
          <div className="text-base text-muted-foreground mb-3">
            {result.min} – {result.max} HP
          </div>
        </>
      )}

      {/* HP bar visual */}
      <div className="relative h-3 bg-muted rounded-full mb-3 overflow-hidden">
        <div
          className="absolute h-full bg-gradient-to-r from-violet-500 to-rose-500 rounded-full opacity-80"
          style={{ left: `${barMin}%`, width: `${Math.max(2, barMax - barMin)}%`, transform: 'translateX(-100%)' }}
        />
        <div
          className="absolute h-full bg-violet-500/40 rounded-full"
          style={{ width: `${barMax}%` }}
        />
      </div>

      {/* KO label */}
      <div className={cn('font-bold text-sm mb-3', ko.cls)}>{ko.label}</div>

      {/* All 16 rolls */}
      {result.rolls && (
        <div className="mb-3">
          <p className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">
            {language === 'es' ? '16 Rolls' : '16 Rolls'}{result.parentalBond ? ` (${language === 'es' ? 'Golpe 1' : 'Hit 1'})` : ''}
          </p>
          <div className="grid grid-cols-8 gap-0.5">
            {result.rolls.map((r, i) => (
              <div
                key={i}
                className={cn(
                  'text-[10px] py-0.5 rounded font-mono',
                  i === 0 ? 'bg-rose-500/20 text-rose-400' :
                  i === 15 ? 'bg-green-500/20 text-green-400' :
                  'bg-muted text-muted-foreground'
                )}
              >
                {r}
              </div>
            ))}
          </div>
          {result.parentalBond && result.hit2Rolls && (
            <>
              <p className="text-[10px] text-muted-foreground mb-1 mt-2 uppercase tracking-wider">
                {language === 'es' ? '16 Rolls (Golpe 2)' : '16 Rolls (Hit 2)'}
              </p>
              <div className="grid grid-cols-8 gap-0.5">
                {result.hit2Rolls.map((r, i) => (
                  <div
                    key={i}
                    className={cn(
                      'text-[10px] py-0.5 rounded font-mono',
                      i === 0 ? 'bg-rose-500/20 text-rose-400' :
                      i === 15 ? 'bg-green-500/20 text-green-400' :
                      'bg-muted text-muted-foreground'
                    )}
                  >
                    {r}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Damage format string */}
      {damageStr && (
        <button
          onClick={handleCopy}
          className="w-full text-left bg-card border border-border rounded-lg px-3 py-2 text-[10px] font-mono text-muted-foreground hover:border-violet-500/50 hover:text-foreground transition-colors break-all"
          title={language === 'es' ? 'Clic para copiar' : 'Click to copy'}
          data-testid="damage-string"
        >
          {copied
            ? (language === 'es' ? '¡Copiado!' : 'Copied!')
            : damageStr}
        </button>
      )}
    </motion.div>
  );
}

export default function DamageCalculator() {
  const { language } = useApp();
  
  // Pokemon state
  const [attacker, setAttacker] = useState(null);
  const [defender, setDefender] = useState(null);
  const [selectedMove, setSelectedMove] = useState(null);
  
  // Stats state
  const [attackerLevel, setAttackerLevel] = useState(50);
  const [defenderLevel, setDefenderLevel] = useState(50);
  const [attackStat, setAttackStat] = useState(100);
  const [defenseStat, setDefenseStat] = useState(100);
  const [defenderHP, setDefenderHP] = useState(200);
  const [attackStage, setAttackStage] = useState(0);
  const [defenseStage, setDefenseStage] = useState(0);

  // Advanced attacker state
  const [attackerNature, setAttackerNature] = useState('hardy');
  const [attackerEV, setAttackerEV] = useState(0);
  const [attackerIV, setAttackerIV] = useState(31);
  const [attackerItem, setAttackerItem] = useState('none');
  const [attackerAbility, setAttackerAbility] = useState('none');
  const [showAdvancedAttacker, setShowAdvancedAttacker] = useState(false);

  // Advanced defender state
  const [defenderNature, setDefenderNature] = useState('hardy');
  const [defenderDefEV, setDefenderDefEV] = useState(0);
  const [defenderDefIV, setDefenderDefIV] = useState(31);
  const [defenderHPEV, setDefenderHPEV] = useState(0);
  const [defenderHPIV, setDefenderHPIV] = useState(31);
  const [showAdvancedDefender, setShowAdvancedDefender] = useState(false);

  // Conditions state
  const [weather, setWeather] = useState('none');
  const [terrain, setTerrain] = useState('none');
  const [critical, setCritical] = useState(false);
  const [burned, setBurned] = useState(false);
  const [screens, setScreens] = useState(false);
  const [isDoubles, setIsDoubles] = useState(false);
  const [lowHPAttacker, setLowHPAttacker] = useState(false);

  // Defender advanced state
  const [defenderAbility, setDefenderAbility] = useState('none');
  const [defenderItem, setDefenderItem] = useState('none');
  const [defenderAtFullHP, setDefenderAtFullHP] = useState(true);

  // Generation selector
  const [generation, setGeneration] = useState(9);

  // Tera Type (Gen 9)
  const [isTera, setIsTera] = useState(false);
  const [teraType, setTeraType] = useState('normal');
  const [defenderTeraActive, setDefenderTeraActive] = useState(false);
  const [defenderTeraType, setDefenderTeraType] = useState('normal');

  // Z-Move (Gen 7+)
  const [isZMove, setIsZMove] = useState(false);

  // Dynamax (Gen 8)
  const [isDynamax, setIsDynamax] = useState(false);

  // Parental Bond
  const [parentalBond, setParentalBond] = useState(false);

  // Entry Hazards (defender side)
  const [stealthRock, setStealthRock] = useState(false);
  const [spikeLayers, setSpikeLayers] = useState(0);

  // Features panel
  const [showFeatures, setShowFeatures] = useState(false);

  // Defender Status
  const [defenderStatus, setDefenderStatus] = useState('none');

  // Update attacker stats when pokemon, move, nature, EV, IV, or level changes
  useEffect(() => {
    if (attacker?.stats) {
      const isPhysical = selectedMove?.damage_class?.name === 'physical';
      const statKey = isPhysical ? 'attack' : 'special-attack';
      const base = attacker.stats.find(s => s.stat.name === statKey);
      if (base) setAttackStat(calcStat(base.base_stat, attackerLevel, attackerEV, attackerIV, attackerNature, statKey));
    }
  }, [attacker, selectedMove, attackerLevel, attackerEV, attackerIV, attackerNature]);

  // Update defender stats when pokemon, move, nature, EV, IV, or level changes
  useEffect(() => {
    if (defender?.stats) {
      const isPhysical = selectedMove?.damage_class?.name === 'physical';
      const defKey = isPhysical ? 'defense' : 'special-defense';
      const defBase = defender.stats.find(s => s.stat.name === defKey);
      const hpBase  = defender.stats.find(s => s.stat.name === 'hp');
      if (defBase) setDefenseStat(calcStat(defBase.base_stat, defenderLevel, defenderDefEV, defenderDefIV, defenderNature, defKey));
      if (hpBase)  setDefenderHP(calcHP(hpBase.base_stat, defenderLevel, defenderHPEV, defenderHPIV));
    }
  }, [defender, selectedMove, defenderLevel, defenderDefEV, defenderDefIV, defenderHPEV, defenderHPIV, defenderNature]);
  
  // Calculate damage
  const damageResult = useMemo(() => {
    if (!attacker || !defender || !selectedMove) return null;

    const attackerTypes = attacker.types?.map(t => t.type.name) || [];
    const defenderTypes = defender.types?.map(t => t.type.name) || [];
    const moveType = selectedMove.type?.name;
    const isPhysical = selectedMove.damage_class?.name === 'physical';
    const hasSTAB = attackerTypes.includes(moveType);
    const typeEff = getTypeEffectiveness(moveType, defenderTypes);

    // Auto-detect move flags from PokeAPI data
    const flags = selectedMove.flags || [];
    const hasFlag = (name) => flags.some(f => f.name === name);
    const isContact       = hasFlag('contact');
    const hasPunchFlag    = hasFlag('punch');
    const hasBiteFlag     = hasFlag('bite');
    const hasPulseFlag    = hasFlag('pulse');
    const hasSoundFlag    = hasFlag('sound');
    // Secondary effect: non-zero chance in meta
    const meta = selectedMove.meta || {};
    const hasSecondaryEffect = (
      (meta.ailment_chance > 0) ||
      (meta.flinch_chance > 0) ||
      (meta.stat_chance > 0)
    );

    return calculateDamage({
      level: attackerLevel,
      power: selectedMove.power || 0,
      attack: attackStat,
      defense: defenseStat,
      attackStage,
      defenseStage,
      stab: hasSTAB,
      typeEffectiveness: typeEff,
      critical,
      weather,
      terrain,
      moveType,
      isPhysical,
      burned,
      screens,
      ability: attackerAbility,
      item: attackerItem,
      targets: isDoubles ? 0.75 : 1,
      lowHPAttacker,
      isContact,
      hasPunchFlag,
      hasBiteFlag,
      hasPulseFlag,
      hasSoundFlag,
      hasSecondaryEffect,
      defenderAbility,
      defenderItem,
      defenderAtFullHP,
      generation,
      parentalBond,
      isZMove: isZMove && generation >= 7,
      isDynamax: isDynamax && generation === 8,
      isTera: isTera && generation === 9,
      teraType,
      attackerOriginalTypes: attackerTypes,
      defenderTeraActive: defenderTeraActive && generation === 9,
      defenderTeraType,
      defenderOriginalTypes: defenderTypes,
    });
  }, [
    attacker, defender, selectedMove, attackerLevel,
    attackStat, defenseStat, attackStage, defenseStage,
    critical, weather, terrain, burned, screens, isDoubles,
    attackerAbility, attackerItem, lowHPAttacker,
    defenderAbility, defenderItem, defenderAtFullHP,
    generation, parentalBond, isZMove, isDynamax,
    isTera, teraType, defenderTeraActive, defenderTeraType,
  ]);

  // Track calculator usage when a result is produced
  useEffect(() => {
    if (damageResult && attacker && defender && selectedMove) {
      trackEvent('calculator_used', {
        attackerName: attacker.name,
        defenderName: defender.name,
        moveName: selectedMove.name,
      });
    }
  }, [damageResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate hazard damage and effective HP for KO calculations
  const hazardDamage = useMemo(() => {
    if (!defender) return 0;
    const defTypes = defender.types?.map(t => t.type.name) || [];
    const isFlying = defTypes.includes('flying');
    const hasLevitate = defenderAbility === 'levitate';
    let dmg = 0;
    // Stealth Rock
    if (stealthRock) {
      const rockEff = getTypeEffectiveness('rock', defTypes);
      dmg += Math.floor(defenderHP * (1/8) * rockEff);
    }
    // Spikes (don't affect Flying or Levitate)
    if (spikeLayers > 0 && !isFlying && !hasLevitate) {
      const spikeMultipliers = [0, 1/8, 1/6, 1/4];
      dmg += Math.floor(defenderHP * (spikeMultipliers[spikeLayers] || 0));
    }
    return dmg;
  }, [defender, defenderHP, stealthRock, spikeLayers, defenderAbility]);

  const effectiveHP = Math.max(1, defenderHP - hazardDamage);

  // Build damage description string
  const damageStr = useMemo(() => buildDamageString({
    attacker, defender, selectedMove,
    attackerEV, attackerNature, attackerItem, attackerAbility,
    defenderHPEV, defenderDefEV, defenderNature,
    defenderItem, defenderAbility,
    defenderHP, result: damageResult, language,
  }), [
    attacker, defender, selectedMove, attackerEV, attackerNature,
    attackerItem, attackerAbility, defenderHPEV, defenderDefEV,
    defenderNature, defenderItem, defenderAbility, defenderHP,
    damageResult, language,
  ]);
  
  // Swap pokemon
  const handleSwap = () => {
    const temp = attacker;
    setAttacker(defender);
    setDefender(temp);
    setSelectedMove(null);
  };
  
  const texts = {
    title: language === 'es' ? 'Calculadora de Daño' : 'Damage Calculator',
    subtitle: language === 'es' ? 'Calcula el daño de tus movimientos' : 'Calculate your move damage',
    attacker: language === 'es' ? 'Atacante' : 'Attacker',
    defender: language === 'es' ? 'Defensor' : 'Defender',
    level: language === 'es' ? 'Nivel' : 'Level',
    attack: language === 'es' ? 'Ataque' : 'Attack',
    defense: language === 'es' ? 'Defensa' : 'Defense',
    hp: 'HP',
    stages: language === 'es' ? 'Etapas' : 'Stages',
    conditions: language === 'es' ? 'Condiciones' : 'Conditions',
    weather: language === 'es' ? 'Clima' : 'Weather',
    terrain: language === 'es' ? 'Terreno' : 'Terrain',
    critical: language === 'es' ? 'Crítico' : 'Critical',
    burned: language === 'es' ? 'Quemado' : 'Burned',
    screens: language === 'es' ? 'Pantallas (Reflect/L.Screen)' : 'Screens (Reflect/L.Screen)',
    doubles: language === 'es' ? 'Combate Doble' : 'Doubles',
    swap: language === 'es' ? 'Intercambiar' : 'Swap'
  };
  
  return (
    <div className="min-h-screen" data-testid="damage-calculator-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">
            <span className="text-violet-600 dark:text-violet-400">{texts.title}</span>
          </h1>
          <p className="text-muted-foreground">{texts.subtitle}</p>
        </motion.div>
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Attacker Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-card rounded-xl border border-border p-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <Swords className="w-5 h-5 text-rose-500" />
              <h2 className="font-semibold">{texts.attacker}</h2>
            </div>
            
            <div className="space-y-4">
              <PokemonSelector 
                pokemon={attacker}
                onSelect={setAttacker}
                label={texts.attacker}
                side="attacker"
              />
              
              <MoveSelector
                pokemon={attacker}
                selectedMove={selectedMove}
                onSelect={setSelectedMove}
              />
              
              <div className="grid grid-cols-2 gap-3">
                <StatInput
                  label={texts.level}
                  value={attackerLevel}
                  onChange={setAttackerLevel}
                  min={1}
                  max={100}
                />
                <StatInput
                  label={selectedMove?.damage_class?.name === 'special' ? 'Sp. Atk' : texts.attack}
                  value={attackStat}
                  onChange={setAttackStat}
                />
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  {texts.attack} {texts.stages}: {attackStage > 0 ? `+${attackStage}` : attackStage}
                </label>
                <Slider
                  value={[attackStage]}
                  onValueChange={([v]) => setAttackStage(v)}
                  min={-6}
                  max={6}
                  step={1}
                  className="py-2"
                />
              </div>
              
              {/* Attacker conditions */}
              <div className="flex flex-wrap gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={burned}
                    onChange={(e) => setBurned(e.target.checked)}
                    className="rounded"
                  />
                  <Flame className="w-4 h-4 text-orange-500" />
                  {texts.burned}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lowHPAttacker}
                    onChange={(e) => setLowHPAttacker(e.target.checked)}
                    className="rounded"
                  />
                  <Droplets className="w-4 h-4 text-red-500" />
                  {language === 'es' ? 'HP bajo (≤1/3)' : 'Low HP (≤1/3)'}
                </label>
                {/* Z-Move (Gen 7+) */}
                {generation >= 7 && generation <= 8 && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isZMove}
                      onChange={(e) => { setIsZMove(e.target.checked); if (e.target.checked) setIsDynamax(false); }}
                      className="rounded"
                    />
                    <span>⚡</span>
                    {language === 'es' ? 'Movimiento Z' : 'Z-Move'}
                  </label>
                )}
                {/* Dynamax (Gen 8) */}
                {generation === 8 && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isDynamax}
                      onChange={(e) => { setIsDynamax(e.target.checked); if (e.target.checked) setIsZMove(false); }}
                      className="rounded"
                    />
                    <span>🔴</span>
                    {language === 'es' ? 'Dinamax' : 'Dynamax'}
                  </label>
                )}
              </div>

              {/* Advanced toggle */}
              <button
                onClick={() => setShowAdvancedAttacker(v => !v)}
                className="w-full flex items-center justify-between text-xs text-violet-600 dark:text-violet-400 font-semibold py-1 hover:opacity-80 transition-opacity"
              >
                <span>{language === 'es' ? 'Avanzado' : 'Advanced'}</span>
                {showAdvancedAttacker ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>

              {showAdvancedAttacker && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 border-t border-border pt-3"
                >
                  {/* Nature */}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">
                      {language === 'es' ? 'Naturaleza' : 'Nature'}
                    </label>
                    <select
                      value={attackerNature}
                      onChange={(e) => setAttackerNature(e.target.value)}
                      className="w-full bg-background border border-input rounded-lg px-2 py-1.5 text-xs"
                    >
                      {NATURE_OPTIONS.map(n => (
                        <option key={n.value} value={n.value}>{language === 'es' ? n.labelEs : n.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* EVs / IVs */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">EVs (0–252)</label>
                      <Input
                        type="number"
                        value={attackerEV}
                        onChange={(e) => setAttackerEV(Math.max(0, Math.min(252, parseInt(e.target.value) || 0)))}
                        className="h-7 text-center text-xs"
                        min={0} max={252} step={4}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">IVs (0–31)</label>
                      <Input
                        type="number"
                        value={attackerIV}
                        onChange={(e) => setAttackerIV(Math.max(0, Math.min(31, parseInt(e.target.value) || 0)))}
                        className="h-7 text-center text-xs"
                        min={0} max={31}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-violet-600 dark:text-violet-400 font-semibold">
                    {language === 'es' ? 'Stat calculado:' : 'Calculated stat:'} {attackStat}
                  </p>

                  {/* Held Item */}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1 flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {language === 'es' ? 'Objeto' : 'Held Item'}
                    </label>
                    <select
                      value={attackerItem}
                      onChange={(e) => setAttackerItem(e.target.value)}
                      className="w-full bg-background border border-input rounded-lg px-2 py-1.5 text-xs"
                    >
                      {HELD_ITEMS.map(it => (
                        <option key={it.value} value={it.value}>{it.label[language] || it.label.en}</option>
                      ))}
                    </select>
                  </div>

                  {/* Ability */}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">
                      {language === 'es' ? 'Habilidad' : 'Ability'}
                    </label>
                    <select
                      value={attackerAbility}
                      onChange={(e) => setAttackerAbility(e.target.value)}
                      className="w-full bg-background border border-input rounded-lg px-2 py-1.5 text-xs"
                    >
                      {ABILITIES.map(ab => (
                        <option key={ab.value} value={ab.value}>{ab.label[language] || ab.label.en}</option>
                      ))}
                    </select>
                  </div>

                  {/* Parental Bond */}
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={parentalBond}
                      onChange={(e) => setParentalBond(e.target.checked)}
                      className="rounded"
                    />
                    <span>👪</span>
                    {language === 'es' ? 'Vínculo Parental (2 golpes)' : 'Parental Bond (2 hits)'}
                  </label>

                  {/* Tera Type (Gen 9 only) */}
                  {generation === 9 && (
                    <div className="space-y-2 p-2 rounded-lg bg-muted/40 border border-border">
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isTera}
                          onChange={(e) => setIsTera(e.target.checked)}
                          className="rounded"
                        />
                        <span>💎</span>
                        {language === 'es' ? 'Terastal activo' : 'Terastal Active'}
                      </label>
                      {isTera && (
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">
                            {language === 'es' ? 'Tipo Tera' : 'Tera Type'}
                          </label>
                          <select
                            value={teraType}
                            onChange={(e) => setTeraType(e.target.value)}
                            className="w-full bg-background border border-input rounded-lg px-2 py-1 text-xs"
                          >
                            {ALL_TYPES.map(t => (
                              <option key={t} value={t}>{TYPE_NAMES[t]?.[language] || t}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
          
          {/* Middle Panel - Results & Conditions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Swap button */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSwap}
                className="gap-2"
                data-testid="swap-btn"
              >
                <RefreshCw className="w-4 h-4" />
                {texts.swap}
              </Button>
            </div>
            
            {/* Damage Result */}
            <DamageResult
              result={damageResult}
              defenderHP={defenderHP}
              damageStr={damageStr}
              immune={damageResult?.immune}
              hazardDamage={hazardDamage}
              defenderStatus={defenderStatus}
              effectiveHP={effectiveHP}
            />
            
            {/* Field Conditions */}
            <div className="bg-card rounded-xl border border-border p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-500" />
                {texts.conditions}
              </h3>
              
              <div className="space-y-3">
                {/* Generation Selector */}
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">
                    🎮 {language === 'es' ? 'Generación' : 'Generation'}
                  </label>
                  <select
                    value={generation}
                    onChange={(e) => {
                      const gen = parseInt(e.target.value);
                      setGeneration(gen);
                      // Reset mechanics not available in selected gen
                      if (gen < 7) { setIsZMove(false); setIsDynamax(false); setIsTera(false); setDefenderTeraActive(false); }
                      if (gen !== 8) setIsDynamax(false);
                      if (gen !== 9) { setIsTera(false); setDefenderTeraActive(false); }
                    }}
                    className="w-full bg-background border border-input rounded-lg px-3 py-2 text-sm"
                    data-testid="generation-select"
                  >
                    {[1,2,3,4,5,6,7,8,9].map(g => (
                      <option key={g} value={g}>Gen {g}</option>
                    ))}
                  </select>
                </div>

                {/* Weather */}
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">{texts.weather}</label>
                  <select
                    value={weather}
                    onChange={(e) => setWeather(e.target.value)}
                    className="w-full bg-background border border-input rounded-lg px-3 py-2 text-sm"
                    data-testid="weather-select"
                  >
                    {WEATHER_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label[language]}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Terrain */}
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">{texts.terrain}</label>
                  <select
                    value={terrain}
                    onChange={(e) => setTerrain(e.target.value)}
                    className="w-full bg-background border border-input rounded-lg px-3 py-2 text-sm"
                    data-testid="terrain-select"
                  >
                    {TERRAIN_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label[language]}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Toggles */}
                <div className="flex flex-wrap gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={critical}
                      onChange={(e) => setCritical(e.target.checked)}
                      className="rounded"
                    />
                    <Zap className="w-4 h-4 text-yellow-500" />
                    {texts.critical}
                  </label>
                  
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isDoubles}
                      onChange={(e) => setIsDoubles(e.target.checked)}
                      className="rounded"
                    />
                    {texts.doubles}
                  </label>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Defender Panel */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-card rounded-xl border border-border p-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-blue-500" />
              <h2 className="font-semibold">{texts.defender}</h2>
            </div>
            
            <div className="space-y-4">
              <PokemonSelector 
                pokemon={defender}
                onSelect={setDefender}
                label={texts.defender}
                side="defender"
              />
              
              <div className="grid grid-cols-3 gap-3">
                <StatInput
                  label={texts.level}
                  value={defenderLevel}
                  onChange={setDefenderLevel}
                  min={1}
                  max={100}
                />
                <StatInput
                  label={selectedMove?.damage_class?.name === 'special' ? 'Sp. Def' : texts.defense}
                  value={defenseStat}
                  onChange={setDefenseStat}
                />
                <StatInput
                  label={texts.hp}
                  value={defenderHP}
                  onChange={setDefenderHP}
                />
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  {texts.defense} {texts.stages}: {defenseStage > 0 ? `+${defenseStage}` : defenseStage}
                </label>
                <Slider
                  value={[defenseStage]}
                  onValueChange={([v]) => setDefenseStage(v)}
                  min={-6}
                  max={6}
                  step={1}
                  className="py-2"
                />
              </div>
              
              {/* Defender conditions */}
              <div className="flex flex-wrap gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={screens}
                    onChange={(e) => setScreens(e.target.checked)}
                    className="rounded"
                  />
                  <Shield className="w-4 h-4 text-blue-400" />
                  {texts.screens}
                </label>
              </div>

              {/* Advanced toggle defender */}
              <button
                onClick={() => setShowAdvancedDefender(v => !v)}
                className="w-full flex items-center justify-between text-xs text-violet-600 dark:text-violet-400 font-semibold py-1 hover:opacity-80 transition-opacity"
              >
                <span>{language === 'es' ? 'Avanzado' : 'Advanced'}</span>
                {showAdvancedDefender ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>

              {showAdvancedDefender && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 border-t border-border pt-3"
                >
                  {/* Nature */}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">
                      {language === 'es' ? 'Naturaleza' : 'Nature'}
                    </label>
                    <select
                      value={defenderNature}
                      onChange={(e) => setDefenderNature(e.target.value)}
                      className="w-full bg-background border border-input rounded-lg px-2 py-1.5 text-xs"
                    >
                      {NATURE_OPTIONS.map(n => (
                        <option key={n.value} value={n.value}>{language === 'es' ? n.labelEs : n.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Defense EVs/IVs */}
                  <div>
                    <p className="text-xs text-muted-foreground font-semibold mb-1">
                      {language === 'es' ? 'EVs/IVs de Defensa' : 'Defense EVs/IVs'}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">EVs</label>
                        <Input
                          type="number"
                          value={defenderDefEV}
                          onChange={(e) => setDefenderDefEV(Math.max(0, Math.min(252, parseInt(e.target.value) || 0)))}
                          className="h-7 text-center text-xs"
                          min={0} max={252} step={4}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">IVs</label>
                        <Input
                          type="number"
                          value={defenderDefIV}
                          onChange={(e) => setDefenderDefIV(Math.max(0, Math.min(31, parseInt(e.target.value) || 0)))}
                          className="h-7 text-center text-xs"
                          min={0} max={31}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-violet-600 dark:text-violet-400 font-semibold mt-1">
                      {language === 'es' ? 'Def calculada:' : 'Calc. Def:'} {defenseStat}
                    </p>
                  </div>

                  {/* HP EVs/IVs */}
                  <div>
                    <p className="text-xs text-muted-foreground font-semibold mb-1">
                      {language === 'es' ? 'EVs/IVs de HP' : 'HP EVs/IVs'}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">EVs</label>
                        <Input
                          type="number"
                          value={defenderHPEV}
                          onChange={(e) => setDefenderHPEV(Math.max(0, Math.min(252, parseInt(e.target.value) || 0)))}
                          className="h-7 text-center text-xs"
                          min={0} max={252} step={4}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">IVs</label>
                        <Input
                          type="number"
                          value={defenderHPIV}
                          onChange={(e) => setDefenderHPIV(Math.max(0, Math.min(31, parseInt(e.target.value) || 0)))}
                          className="h-7 text-center text-xs"
                          min={0} max={31}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-violet-600 dark:text-violet-400 font-semibold mt-1">
                      {language === 'es' ? 'HP calculado:' : 'Calc. HP:'} {defenderHP}
                    </p>
                  </div>

                  {/* Defender full HP toggle */}
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={defenderAtFullHP}
                      onChange={e => setDefenderAtFullHP(e.target.checked)}
                      className="rounded"
                    />
                    {language === 'es' ? 'HP lleno (Multiscala)' : 'Full HP (Multiscale)'}
                  </label>

                  {/* Defender Ability */}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">
                      {language === 'es' ? 'Habilidad defensora' : 'Defender Ability'}
                    </label>
                    <select
                      value={defenderAbility}
                      onChange={e => setDefenderAbility(e.target.value)}
                      className="w-full bg-background border border-input rounded-lg px-2 py-1.5 text-xs"
                    >
                      {DEFENDER_ABILITIES.map(ab => (
                        <option key={ab.value} value={ab.value}>{ab.label[language] || ab.label.en}</option>
                      ))}
                    </select>
                  </div>

                  {/* Defender Item */}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1 flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {language === 'es' ? 'Objeto defensivo' : 'Defender Item'}
                    </label>
                    <select
                      value={defenderItem}
                      onChange={e => setDefenderItem(e.target.value)}
                      className="w-full bg-background border border-input rounded-lg px-2 py-1.5 text-xs"
                    >
                      {DEFENDER_ITEMS.map(it => (
                        <option key={it.value} value={it.value}>{it.label[language] || it.label.en}</option>
                      ))}
                    </select>
                  </div>

                  {/* Defender Tera Type (Gen 9 only) */}
                  {generation === 9 && (
                    <div className="space-y-2 p-2 rounded-lg bg-muted/40 border border-border">
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={defenderTeraActive}
                          onChange={(e) => setDefenderTeraActive(e.target.checked)}
                          className="rounded"
                        />
                        <span>💎</span>
                        {language === 'es' ? 'Defensor Terastal' : 'Defender Terastal'}
                      </label>
                      {defenderTeraActive && (
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">
                            {language === 'es' ? 'Tipo Tera Defensor' : 'Defender Tera Type'}
                          </label>
                          <select
                            value={defenderTeraType}
                            onChange={(e) => setDefenderTeraType(e.target.value)}
                            className="w-full bg-background border border-input rounded-lg px-2 py-1 text-xs"
                          >
                            {ALL_TYPES.map(t => (
                              <option key={t} value={t}>{TYPE_NAMES[t]?.[language] || t}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Entry Hazards */}
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-semibold">
                      🪨 {language === 'es' ? 'Trampas de entrada' : 'Entry Hazards'}
                    </p>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={stealthRock}
                        onChange={(e) => setStealthRock(e.target.checked)}
                        className="rounded"
                      />
                      {language === 'es' ? 'Roca Afilada (Stealth Rock)' : 'Stealth Rock'}
                    </label>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">
                        {language === 'es' ? 'Capas de Trampa (Spikes)' : 'Spikes layers'}
                      </label>
                      <select
                        value={spikeLayers}
                        onChange={(e) => setSpikeLayers(parseInt(e.target.value))}
                        className="w-full bg-background border border-input rounded-lg px-2 py-1 text-xs"
                      >
                        <option value={0}>{language === 'es' ? 'Sin trampas' : 'No Spikes'}</option>
                        <option value={1}>{language === 'es' ? '1 capa (1/8 HP)' : '1 layer (1/8 HP)'}</option>
                        <option value={2}>{language === 'es' ? '2 capas (1/6 HP)' : '2 layers (1/6 HP)'}</option>
                        <option value={3}>{language === 'es' ? '3 capas (1/4 HP)' : '3 layers (1/4 HP)'}</option>
                      </select>
                    </div>
                  </div>

                  {/* Defender Status */}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">
                      💊 {language === 'es' ? 'Estado del defensor' : 'Defender Status'}
                    </label>
                    <select
                      value={defenderStatus}
                      onChange={(e) => setDefenderStatus(e.target.value)}
                      className="w-full bg-background border border-input rounded-lg px-2 py-1.5 text-xs"
                    >
                      <option value="none">{language === 'es' ? 'Sano' : 'Healthy'}</option>
                      <option value="poison">{language === 'es' ? 'Envenenado (1/8 HP/turno)' : 'Poisoned (1/8 HP/turn)'}</option>
                      <option value="badly_poisoned">{language === 'es' ? 'Muy envenenado (Tóxico ~3/16)' : 'Badly Poisoned (Toxic ~3/16)'}</option>
                      <option value="burn">{language === 'es' ? 'Quemado (1/16 HP/turno)' : 'Burned (1/16 HP/turn)'}</option>
                    </select>
                    {defenderStatus !== 'none' && (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {defenderStatus === 'badly_poisoned'
                          ? (language === 'es' ? '* Tóxico: ~3/16 HP promedio en 3 turnos' : '* Toxic: ~3/16 HP avg over 3 turns')
                          : (language === 'es'
                            ? `Residual: ${defenderStatus === 'poison' ? Math.floor(defenderHP / 8) : Math.floor(defenderHP / 16)} HP/turno`
                            : `Residual: ${defenderStatus === 'poison' ? Math.floor(defenderHP / 8) : Math.floor(defenderHP / 16)} HP/turn`)
                        }
                      </p>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Defender type display */}
              {defender && (
                <div className="pt-2 border-t border-border">
                  <label className="text-xs text-muted-foreground block mb-2">
                    {language === 'es' ? 'Tipos' : 'Types'}
                  </label>
                  <div className="flex gap-2">
                    {defender.types?.map(t => (
                      <span
                        key={t.type.name}
                        className={cn(
                          'px-2 py-1 rounded text-xs font-semibold uppercase',
                          TYPE_COLORS[t.type.name] || 'bg-gray-400',
                          ['electric', 'ice', 'flying', 'fairy', 'steel', 'normal'].includes(t.type.name) ? 'text-gray-900' : 'text-white'
                        )}
                      >
                        {TYPE_NAMES[t.type.name]?.[language] || t.type.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
        
        {/* Features Panel (collapsible, closed by default) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 bg-card rounded-xl border border-border overflow-hidden"
        >
          <button
            onClick={() => setShowFeatures(v => !v)}
            className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-violet-500" />
              <h3 className="font-semibold text-sm">
                {language === 'es' ? 'Características de la calculadora' : 'Calculator Features'}
              </h3>
            </div>
            {showFeatures ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>

          <AnimatePresence>
            {showFeatures && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      language === 'es' ? 'Fórmula Gen 5+ (SV)' : 'Gen 5+ Formula (SV)',
                      language === 'es' ? 'Naturaleza (×1.1/×0.9)' : 'Nature (×1.1/×0.9)',
                      language === 'es' ? 'EVs / IVs exactos' : 'Exact EVs / IVs',
                      language === 'es' ? '16 rolls de daño' : '16 Damage rolls',
                      language === 'es' ? 'String de daño copiable' : 'Copyable damage string',
                      language === 'es' ? 'Objetos atacante (Orbe, Choice...)' : 'Attacker items (Orb, Choice...)',
                      language === 'es' ? 'Objetos defensor (Assault Vest, Eviolite)' : 'Defender items (Assault Vest, Eviolite)',
                      language === 'es' ? 'Habilidades atacante (40+)' : 'Attacker abilities (40+)',
                      language === 'es' ? 'Habilidades defensor (Multiscale, Filter...)' : 'Defender abilities (Multiscale, Filter...)',
                      language === 'es' ? 'Auto-detección flags (Puño, Mordida, Pulso, Sonido)' : 'Auto move flags (Punch, Bite, Pulse, Sound)',
                      language === 'es' ? 'Clima & Terreno' : 'Weather & Terrain',
                      language === 'es' ? 'Golpe Crítico' : 'Critical Hit',
                      language === 'es' ? 'STAB (Adaptabilidad ×2)' : 'STAB (Adaptability ×2)',
                      language === 'es' ? 'Combate Doble (×0.75)' : 'Doubles (×0.75)',
                      language === 'es' ? 'Pantallas (Reflect/L.Screen)' : 'Screens (Reflect/L.Screen)',
                      language === 'es' ? 'Todos los movimientos del Pokémon' : 'All Pokémon moves loaded',
                      language === 'es' ? 'Inmunidades por habilidad' : 'Ability immunities',
                      language === 'es' ? 'Selección de generación (Gen 1-9)' : 'Generation selection (Gen 1-9)',
                      language === 'es' ? 'Formas especiales (Mega, Alolan, Tera)' : 'Special forms (Mega, Alolan, Tera)',
                      language === 'es' ? 'Tipo Tera (Gen 9)' : 'Tera Type (Gen 9)',
                      language === 'es' ? 'Movimiento Z (Gen 7)' : 'Z-Moves (Gen 7)',
                      language === 'es' ? 'Dinamax (Gen 8)' : 'Dynamax (Gen 8)',
                      language === 'es' ? 'Vínculo Parental (2 golpes)' : 'Parental Bond (2 hits)',
                      language === 'es' ? 'Trampas de entrada (Roca/Trampa)' : 'Entry Hazards (SR/Spikes)',
                      language === 'es' ? 'Estado del defensor (veneno/quemadura)' : 'Defender Status (poison/burn)',
                    ].map((feat) => (
                      <div key={feat} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="text-green-500">✓</span>
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
